/**
 * OpsPropertiesView — Operations Dashboard read-only view of Properties
 * + their Service Agreements (contracts). All pricing, contract upload,
 * and agreement editing lives in HR / Finance / Legal / Sales
 * departments — Ops just needs to see the roster.
 *
 * Data comes from parent (`properties`, `contracts`). We do not fetch on
 * our own so the parent stays the single source of truth.
 */
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Building2, Search, FileText, MapPin, Package, QrCode, LayoutGrid, Dog, Trash2, Recycle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import QRCodesTab from './QRCodesTab';
import { CommercialHandoffBoard } from './admin/CommercialHandoffBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Human-friendly labels for our internal service-package codes. Falls
// back to the raw code (title-cased) for anything we don't recognize.
const PACKAGE_LABELS = {
  bronze: 'Bronze — Standard valet trash',
  silver: 'Silver — Standard + dumpster hygiene',
  gold: 'Gold — Full-service (pressure wash + eco)',
  platinum: 'Platinum — All-inclusive premium',
  custom: 'Custom package',
};
function packageLabel(code) {
  if (!code) return null;
  const k = String(code).toLowerCase();
  return PACKAGE_LABELS[k] || code.charAt(0).toUpperCase() + code.slice(1);
}

export default function OpsPropertiesView({ properties = [], contracts = [], viewAs = 'corporate' }) {
  const [q, setQ] = useState('');
  const [qrPropertyId, setQrPropertyId] = useState(null);
  const [structureProperty, setStructureProperty] = useState(null);
  const [structure, setStructure] = useState(null);

  const openStructure = async (p) => {
    setStructureProperty(p);
    setStructure(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/properties/${p.property_id}/structure`, { credentials: 'include' });
      if (res.ok) setStructure(await res.json());
    } catch (e) { console.error('Failed to load structure:', e); }
  };

  // Contracts indexed by property_id for quick lookup — one property may
  // have multiple agreements (base MSA, addendum, etc.).
  const contractsByProperty = useMemo(() => {
    const map = {};
    (contracts || []).forEach((c) => {
      const pid = c.property_id;
      if (!pid) return;
      if (!map[pid]) map[pid] = [];
      map[pid].push(c);
    });
    return map;
  }, [contracts]);

  const filtered = useMemo(() => {
    if (!q) return properties;
    const needle = q.toLowerCase();
    return properties.filter((p) => {
      const hay = `${p.name || ''} ${p.property_name || ''} ${p.address || ''} ${p.city || ''} ${p.region_code || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [properties, q]);

  return (
    <div className="space-y-4" data-testid="ops-properties-view">
      <CommercialHandoffBoard viewAs={viewAs} />
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Building2 className="h-6 w-6 text-[#C9A962]" />
              PROPERTIES & SERVICE AGREEMENTS · READ-ONLY
            </h2>
            <p className="text-xs text-zinc-400 mt-1">
              Operations view · Pricing edits &amp; contract uploads live in
              <span className="text-[#C9A962] font-semibold"> HR / Finance / Legal / Sales Command Centers</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-[#C9A962] text-black font-bold">{filtered.length} properties</Badge>
            <Badge className="bg-zinc-700 text-white">{(contracts || []).length} agreements</Badge>
          </div>
        </div>

        <div className="relative mt-4 max-w-md">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search property, address, region…"
            className="pl-8 bg-zinc-950 border-zinc-700 text-white"
            data-testid="ops-properties-search"
          />
        </div>
      </Card>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        {filtered.length === 0 && (
          <Card className="bg-zinc-900 border-zinc-700 p-8 col-span-full text-center text-zinc-500"
                data-testid="ops-properties-empty">
            No properties match the search.
          </Card>
        )}
        {filtered.map((p) => {
          const pcs = contractsByProperty[p.property_id] || [];
          return (
            <Card key={p.property_id}
                  className="bg-zinc-900 border-zinc-700 p-4 min-w-0"
                  data-testid={`ops-property-${p.property_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1 basis-40">
                  <h3 className="text-white font-bold break-words [overflow-wrap:anywhere]" data-testid={`ops-property-name-${p.property_id}`}>{p.name || p.property_name || p.address || 'Unnamed property'}</h3>
                  <div className="text-xs text-zinc-400 flex items-start gap-1 mt-0.5 break-words [overflow-wrap:anywhere]">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {[p.address, p.city].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0 max-w-full">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => openStructure(p)}
                    data-testid={`ops-property-structure-btn-${p.property_id}`}
                    title="View property structure"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
                    onClick={() => setQrPropertyId(p.property_id)}
                    data-testid={`ops-property-qr-btn-${p.property_id}`}
                    title="View QR codes"
                  >
                    <QrCode className="h-3.5 w-3.5" />
                  </Button>
                  <Badge data-testid={`ops-property-status-${p.property_id}`} className={`${p.status === 'active' ? 'bg-emerald-700' : 'bg-zinc-700'} max-w-full whitespace-normal break-words [overflow-wrap:anywhere]`}>
                    {p.status || 'active'}
                  </Badge>
                </div>
              </div>

              {/* Region + contracted service package — two chips so Ops
                  can dispatch the right crew to the right zone at a
                  glance. Both are surfaced for EVERY property on file;
                  when a value is missing we render a dim "—" chip so
                  the layout stays stable. */}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <div className="bg-zinc-800/80 border border-zinc-700 rounded-md px-2 py-1 flex items-center gap-1.5"
                     data-testid={`ops-property-region-${p.property_id}`}>
                  <MapPin className="h-3 w-3 text-[#C9A962]" />
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">Region</span>
                  <span className="text-xs font-semibold text-white font-mono">
                    {p.region_code || '—'}
                  </span>
                </div>
                <div className={`rounded-md px-2 py-1 flex flex-wrap items-center gap-1.5 border max-w-full min-w-0 [overflow-wrap:anywhere] ${
                  (p.service_package || p.package || p.tier)
                    ? 'bg-[#C9A962]/10 border-[#C9A962]/40'
                    : 'bg-zinc-800/80 border-zinc-700'
                }`}
                     data-testid={`ops-property-package-${p.property_id}`}>
                  <Package className={`h-3 w-3 ${(p.service_package || p.package || p.tier) ? 'text-[#C9A962]' : 'text-zinc-500'}`} />
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">Package</span>
                  <span className={`text-xs font-semibold ${
                    (p.service_package || p.package || p.tier) ? 'text-[#C9A962]' : 'text-zinc-500'
                  }`}>
                    {packageLabel(p.service_package || p.package || p.tier) || '— not set —'}
                  </span>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-2 mt-2">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
                  <FileText className="h-3 w-3" /> Service Agreements ({pcs.length})
                </div>
                {pcs.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">No agreements on file.</div>
                ) : (
                  <ul className="space-y-1">
                    {pcs.map((c) => (
                      <li key={c.contract_id}
                          className="text-xs text-zinc-200 flex items-center justify-between gap-2">
                        <span className="truncate">
                          {c.title || c.file_name || 'Service Agreement'}
                        </span>
                        <span className="text-zinc-500 flex-shrink-0">
                          {c.uploaded_at ? new Date(c.uploaded_at).toLocaleDateString() : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Property structure drill-down — buildings, units, amenities. */}
      <Dialog open={!!structureProperty} onOpenChange={(o) => !o && setStructureProperty(null)}>
        <DialogContent
          className="max-w-3xl bg-zinc-950 border-blue-500 text-white max-h-[90vh] overflow-y-auto"
          data-testid="ops-property-structure-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-blue-400 flex items-center gap-2">
              <LayoutGrid className="h-5 w-5" />
              {structureProperty?.property_name || structureProperty?.name || 'Property'} · Structure
            </DialogTitle>
          </DialogHeader>
          {!structure ? (
            <p className="text-sm text-zinc-400">Loading structure…</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { label: 'Buildings', value: (structure.buildings || []).length, icon: Building2 },
                  { label: 'Total Units', value: structure.total_units, icon: LayoutGrid },
                  { label: 'Trash Areas', value: structure.trash_areas_count, icon: Trash2 },
                  { label: 'Pet Stations', value: structure.pet_stations_count, icon: Dog },
                  { label: 'Dumpsters', value: structure.dumpsters_count, icon: Trash2 },
                ].map((kpi) => (
                  <div key={kpi.label} className="bg-zinc-900 border border-zinc-700 rounded-md p-2.5 text-center"
                       data-testid={`structure-kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <kpi.icon className="h-4 w-4 mx-auto text-blue-400 mb-1" />
                    <p className="text-lg font-black text-white leading-none">{kpi.value ?? '—'}</p>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{kpi.label}</p>
                  </div>
                ))}
              </div>

              {/* Feb 2026 — Recycling Service admin toggle */}
              <RecyclingToggle
                propertyId={structureProperty.property_id}
                structure={structure}
                onChange={(next) => setStructure({ ...structure, ...next })}
              />

              <div className="space-y-2" data-testid="structure-buildings-list">
                {(structure.buildings || []).map((b) => (
                  <div key={b.building_number} className="bg-zinc-900 border border-zinc-700 rounded-md p-3"
                       data-testid={`structure-building-${b.building_number}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-white">Building {b.building_number}</p>
                      <Badge className="bg-blue-600 text-white text-[10px]">{(b.units || []).length} units</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(b.units || []).map((u) => (
                        <span key={u} className="px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-[10px] font-mono text-zinc-300">
                          {u}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {structure.auto_generated && (
                <p className="text-[10px] text-zinc-500">
                  Structure auto-generated from total buildings/units — Admin can refine it via the property structure API.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-property QR code dialog — locked to the property clicked. */}      <Dialog open={!!qrPropertyId} onOpenChange={(o) => !o && setQrPropertyId(null)}>
        <DialogContent
          className="max-w-4xl bg-zinc-950 border-[#C9A962] text-white max-h-[90vh] overflow-y-auto"
          data-testid="ops-property-qr-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[#C9A962] flex items-center gap-2">
              <QrCode className="h-5 w-5" /> Property &amp; Unit QR Codes
            </DialogTitle>
          </DialogHeader>
          {qrPropertyId && (
            <QRCodesTab lockedPropertyId={qrPropertyId} dataTestId="ops-property-qr-tab" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Feb 2026 — Recycling Service admin toggle.
 * Compact PATCH-based control that flips `has_recycling_service` and
 * updates the recycling dumpster count. Only PMs/Admins reach this view
 * (route-gated upstream). Saves via PUT /properties/{id}/structure.
 */
function RecyclingToggle({ propertyId, structure, onChange }) {
  const enabled = !!structure.has_recycling_service;
  const [count, setCount] = useState(structure.recycling_dumpster_count ?? 0);
  const [saving, setSaving] = useState(false);

  const save = async (nextEnabled, nextCount) => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/structure`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          has_recycling_service: nextEnabled,
          recycling_dumpster_count: nextEnabled ? Math.max(0, Number(nextCount) || 0) : 0,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Could not update recycling settings');
        return;
      }
      onChange({
        has_recycling_service: nextEnabled,
        recycling_dumpster_count: nextEnabled ? Math.max(0, Number(nextCount) || 0) : 0,
      });
      toast.success(nextEnabled ? 'Recycling service enabled' : 'Recycling service disabled');
    } catch (e) {
      toast.error('Could not update recycling settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between bg-emerald-950/30 border border-emerald-700/40 rounded-md p-3"
      data-testid="recycling-toggle-card"
    >
      <div className="flex items-center gap-3">
        <Recycle className={`h-5 w-5 ${enabled ? 'text-emerald-400' : 'text-zinc-500'}`} />
        <div>
          <p className="text-sm font-bold text-white">Recycling Service</p>
          <p className="text-[11px] text-zinc-400">
            {enabled ? 'Agents can log recycling pickups at this property.' : 'Disabled — agents cannot record recycling.'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {enabled && (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={0}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              onBlur={() => save(true, count)}
              className="w-16 h-8 text-xs bg-zinc-900 border-zinc-700 text-white"
              data-testid="recycling-dumpster-count"
              aria-label="Recycling dumpsters"
            />
            <span className="text-[11px] text-zinc-400">bins</span>
          </div>
        )}
        <Button
          size="sm"
          onClick={() => save(!enabled, count)}
          disabled={saving}
          className={enabled ? 'bg-red-600 hover:bg-red-700 text-white text-xs h-8' : 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8'}
          data-testid="recycling-toggle-btn"
        >
          {saving ? 'Saving…' : enabled ? 'Disable' : 'Enable'}
        </Button>
      </div>
    </div>
  );
}

